import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Honeybadger } from '@honeybadger-io/react';
import HyperDX from '@hyperdx/browser';
import Clarity from '@microsoft/clarity';
import { ConnectedRouter } from 'connected-react-router';
import { useEffect, useContext } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import TagManager from 'react-gtm-module';
import { Provider } from 'react-redux';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { AnalyticsProvider, FallbackErrorScreen, browserHistory, ToastDismissButton, TooZoom, TestEnvironmentBanner, useErrorListener, AnalyticsContext, getTestEnv, REACT_APP_HYPERDX, CREDIT_PORTAL_URL, REACT_APP_RECAPTCHA_SITE_KEY, } from 'shared';
import { VersionChangeReporter } from 'components';
import { consumerApplicationsApi } from 'consumer-applications-v2-api';
import store from 'store';
import Routes from './Routes';
function App() {
    useErrorListener();
    const { setAppName } = useContext(AnalyticsContext);
    const testEnv = getTestEnv();
    useEffect(() => {
        if (process.env.REACT_APP_GTM_ID) {
            TagManager.initialize({
                gtmId: process.env.REACT_APP_GTM_ID,
                events: {
                    conversion: 'conversion',
                },
            });
        }
        Honeybadger.configure({
            apiKey: process.env.REACT_APP_HONEYBADGER_API_KEY,
            environment: process.env.REACT_APP_BUILD_ENV,
        });
        let checkRegEx = null;
        if (testEnv) {
            checkRegEx = [/api.qa.alphaeontest.com/i];
        }
        else {
            checkRegEx = [/api.alphaeon.com/i];
        }
        const buildEnv = process.env.REACT_APP_BUILD_ENV;
        const isProd = buildEnv === 'production';
        const isQa = buildEnv === 'qa';
        const startHyperDx = () => {
            HyperDX.init({
                apiKey: REACT_APP_HYPERDX,
                service: isProd ? 'credit-portal' : 'credit-portal-qa',
                tracePropagationTargets: checkRegEx, // Set to link traces from frontend to backend requests
                consoleCapture: true, // Capture console logs (default false)
                advancedNetworkCapture: true, // Capture full HTTP request/response headers and bodies (default false)
            });
            HyperDX.setGlobalAttributes({
                site_environment: testEnv ? 'DEV' : 'PROD',
                site_portal: 'credit-portal',
                userAgent: window.navigator.userAgent,
            });
        };
        if (isProd) {
            startHyperDx();
        }
        else if (isQa) {
            // QA is gated on the consumer_qa_hyperdx Flipper flag (toggle from Rails console).
            store
                .dispatch(consumerApplicationsApi.endpoints.getConsumerApplicationsV2FeatureFlagsGlobal.initiate({}))
                .then((result) => {
                const flags = result.data?.featureFlags;
                if (flags?.consumerQaHyperdx)
                    startHyperDx();
            });
        }
        const clarityProjectId = process.env.REACT_APP_CLARITY_PROJECT_ID;
        if (isProd && clarityProjectId) {
            Clarity.init(clarityProjectId);
        }
        // AnalyticsContext
        if (setAppName) {
            setAppName('credit-portal');
        }
    }, []);
    return (_jsx(ErrorBoundary, { fallbackRender: ({ error }) => _jsx(FallbackErrorScreen, { error: error }), children: _jsx(Provider, { store: store, children: _jsxs(ConnectedRouter, { history: browserHistory, children: [_jsx(ToastContainer, { closeButton: ToastDismissButton }), _jsx(AnalyticsProvider, { appName: "credit-portal", children: _jsxs(GoogleReCaptchaProvider, { reCaptchaKey: REACT_APP_RECAPTCHA_SITE_KEY, scriptProps: { async: true, defer: true, appendTo: 'body' }, children: [testEnv && _jsx(TestEnvironmentBanner, { env: testEnv }), _jsx(Routes, {}), _jsx(TooZoom, {}), _jsx(VersionChangeReporter, { redirectURL: CREDIT_PORTAL_URL })] }) })] }) }) }));
}
export default App;
